import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Star, Quote } from "lucide-react";

interface Testimonial {
  id: string;
  client_name: string;
  company?: string;
  position?: string;
  testimonial: string;
  avatar_url?: string;
  rating?: number;
  testimonial_i18n?: Record<string, string>;
  position_i18n?: Record<string, string>;
}

const TestimonialsSection = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);

  useEffect(() => {
    const fetchTestimonials = async () => {
      const { data } = await supabase
        .from('testimonials')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(6);

      if (data) setTestimonials(data as Testimonial[]);
    };

    fetchTestimonials();
  }, []);

  if (testimonials.length === 0) return null;

  return (
    <section className="py-20 bg-muted/30">
      <div className="container">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t("testimonials.title")}</h2>
          <p className="text-muted-foreground text-lg">{t("testimonials.subtitle")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((testimonial) => {
            const text = testimonial.testimonial_i18n?.[lang]?.trim() || testimonial.testimonial;
            const position = testimonial.position_i18n?.[lang]?.trim() || testimonial.position;
            return (
            <Card key={testimonial.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <Quote className="h-8 w-8 text-brand-yellow mb-4" />
                <p className="text-muted-foreground mb-6 line-clamp-4">{text}</p>
                
                <div className="flex items-center gap-4">
                  {testimonial.avatar_url ? (
                    <img 
                      src={testimonial.avatar_url} 
                      alt={testimonial.client_name}
                      className="w-12 h-12 rounded-full object-cover"
                      loading="lazy"
                      width="48"
                      height="48"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-brand-yellow/20 flex items-center justify-center text-lg font-semibold">
                      {testimonial.client_name.charAt(0)}
                    </div>
                  )}
                  
                  <div className="flex-1">
                    <p className="font-semibold">{testimonial.client_name}</p>
                    {position && testimonial.company && (
                      <p className="text-sm text-muted-foreground">
                        {position} at {testimonial.company}
                      </p>
                    )}
                    {testimonial.rating && (
                      <div className="flex items-center gap-1 mt-1">
                        {Array.from({ length: testimonial.rating }).map((_, i) => (
                          <Star key={i} className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;